import ExpandIcon from '@cp/src/assets/icons/ExpandIcon';
import Dropdown from '@cp/src/components/Dropdown';
import { INumberDropdown, IPhoneNumber, IPhoneNumberStatus } from '../type';
import styles from '../index.module.scss';
import React from 'react';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import Button from '@navi/web-ui/lib/primitives/Button/Button';
import { SelectPickerOptionProps } from '@cp/src/components/interfaces';
import cx from 'classnames';
import CollapseIcon from '@cp/src/assets/icons/CollapseIcon';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { getPhoneNumberStatus } from '../actions';
import { useParams } from 'react-router-dom';
import { NUMBER_FILTERS } from '../constants';

const NumberDropDown: React.FC<INumberDropdown> = props => {
  const { isOnModal, onCloseModal, queryParams, handleFilterChange, setIsExpandedView } = props;

  let allNumbersList: IPhoneNumber[] = [];
  let allIdentifiers: string[] = [];
  const {
    loanId = '',
    customerId = '',
    caseBusinessVertical = '',
    caseReferenceId = ''
  } = useParams();

  const aggregationNumbers = useSelector(
    (state: RootState) => state.bankStatementSlice.bankStatementNumbers
  );
  const [allNumberStatus, setAllNumberStatus] = React.useState<IPhoneNumberStatus[]>([]);
  allIdentifiers = aggregationNumbers.map(number => number.identifier);
  const handleFetchNumberStatus = () => {
    getPhoneNumberStatus({
      lan: loanId,
      customerId: customerId,
      phoneNumbers: allIdentifiers,
      caseReferenceId,
      caseBusinessVertical
    }).then(response => {
      setAllNumberStatus(response);
    });
  };

  const PhoneNumberList = () => {
    const numberListWithAggregation = aggregationNumbers.map(aggregation => {
      const status =
        allNumberStatus.find(number => number.numberCustomId === aggregation.identifier)
          ?.validity || '';
      const phoneNumber = aggregation.text;
      const repeatedNumber = aggregation.occurrenceCount;
      const identifier = aggregation.identifier;
      const isExisting = status;
      return { phoneNumber, repeatedNumber, identifier, isExisting };
    });

    const sortedNumberList = numberListWithAggregation.sort(
      (a, b) => b.repeatedNumber - a.repeatedNumber
    );

    allNumbersList = sortedNumberList.map(number => ({
      value: number.phoneNumber,
      label: number.phoneNumber,
      identifier: number.identifier,
      repeatedNumber: number.repeatedNumber,
      isExisting: number.isExisting
    }));

    const filteredNumbersList = [];

    filteredNumbersList.push({
      value: NUMBER_FILTERS.ALL_NUMBERS,
      label: NUMBER_FILTERS.ALL_NUMBERS,
      repeatedNumber: null,
      isExisting: null
    });

    allNumbersList.forEach(number => {
      if (number.repeatedNumber > 3) {
        filteredNumbersList.push(number);
      }
    });
    const otherNumbers = allNumbersList.filter(number => number.repeatedNumber < 3);

    if (otherNumbers.length > 0) {
      filteredNumbersList.push({
        value: NUMBER_FILTERS.OTHER_NUMBERS,
        label: NUMBER_FILTERS.OTHER_NUMBERS,
        repeatedNumber: null
      });
    }

    return filteredNumbersList || [];
  };

  const handleNumberSelection = (option: SelectPickerOptionProps) => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_DOCUMENTS_BANK_STATEMENT_FILTER_NUMBER);
    handleFilterChange(String(option.value), option.identifier);
  };
  const handleRemoveFilters = (option: SelectPickerOptionProps) => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_DOCUMENTS_BANK_STATEMENT_REMOVE_FILTERS);
    handleFilterChange(option.value, option.identifier);
  };

  return (
    <div className="flex flex-row items-center gap-[12px]">
      {aggregationNumbers.length > 0 && (
        <div onClick={handleFetchNumberStatus}>
          <Dropdown
            options={PhoneNumberList()}
            placeholder="Find Number"
            multiSelect={false}
            showSolidArrow={true}
            arrowIconWidth={9}
            containerClasses="h-[36px]"
            innerContainerStyle={cx(styles.dropDownSelector, {
              '!bg-bg-blue': queryParams?.filterNumber
            })}
            titleClass="!text-blue-base flex justify-between gap-[4px]"
            pickerContainerClasses={cx('w-[300px] translate-x-[-57%]', {
              '!translate-x-[-19%]': queryParams?.filterNumber
            })}
            arrowIconColor="var(--blue-base)"
            showCancelOption={true}
            showTitleInSelectedValue={true}
            defaultSelected={
              queryParams?.filterNumber
                ? { value: queryParams.filterNumber, label: queryParams.filterNumber }
                : undefined
            }
            onSelectionChange={handleNumberSelection}
            onRemoveFilters={handleRemoveFilters}
            customOptionTemplate={(option, metadata) => {
              return metadata?.repeatedNumber ? (
                <div className="flex flex-col gap-[4px] justify-between w-[100%]">
                  <div className="flex flex-row justify-between">
                    <Typography variant="p3" className="text-grayscale-2">
                      {option}
                    </Typography>
                    <div className="flex row gap-[4px] items-center">
                      <Typography variant="p5" className="text-grayscale-2">
                        (repeated
                      </Typography>
                      <Typography variant="h5" className="text-grayscale-1">
                        {metadata?.repeatedNumber}
                      </Typography>
                      <Typography variant="p5" className="text-grayscale-2">
                        times)
                      </Typography>
                    </div>
                  </div>
                  <Typography variant="p5" className="text-grayscale-2">
                    {metadata?.isExisting === 'EXISTS_AND_VALID'
                      ? 'Number Added'
                      : metadata?.isExisting === 'EXISTS_AND_INVALID'
                      ? 'Number Invalid'
                      : ''}
                  </Typography>
                </div>
              ) : option === 'Other numbers' ? (
                <div className="flex flex-row justify-between w-[100%] items-center">
                  <Typography variant="p3" className="text-grayscale-2">
                    {option}
                  </Typography>
                  <Typography variant="p5" className="text-grayscale-2">
                    (repeated less frequently)
                  </Typography>
                </div>
              ) : (
                <Typography variant="p3" className="text-grayscale-2">
                  {option}
                </Typography>
              );
            }}
          />
        </div>
      )}

      <div className="w-[1px] h-[20px] bg-greyscale-light" />

      {!isOnModal ? (
        <>
          <Button
            variant="text"
            onClick={() => {
              setIsExpandedView(true);
              addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_DOCUMENTS_BANK_STATEMENT_EXPANDVIEW);
            }}
          >
            <ExpandIcon />
          </Button>
        </>
      ) : (
        <Button
          variant="text"
          onClick={() => {
            setIsExpandedView(false);
            onCloseModal();
            addClickstreamEvent(
              CLICKSTREAM_EVENT_NAMES.LH_DOCUMENTS_BANK_STATEMENT_COLLAPSED_FROM_EXPAND
            );
          }}
        >
          <CollapseIcon />
        </Button>
      )}
    </div>
  );
};

export default NumberDropDown;
